import React, { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import ConfigurableTable from '@uikit/old/ConfigurableTable/ConfigurableTable';
import Loader from '@uikit/old/Loader/Loader';
import NoAccessMessage from '@uikit/old/NoAccessMessage/NoAccessMessage';
import Pagination from '@uikit/old/Pagination/Pagination';
import usePagination from '@hooks/usePagination';
import useNonInitialEffect from '@hooks/useNonInitialEffect';

import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import queryCacheKeys from '@constants/queryCacheKeys.constants';

import {
  changePublisherWebsiteStatusApi,
  deletePublisherWebsiteApi,
  getPublisherWebsitesApi,
} from '@api/publishers.api';
import tableConfig, { RenderRow } from './tableConfig';

import * as common_pb from 'respona_api/generated/common_pb';
import { addNotification } from '@redux/actions/notifications.actions';

import { createUpdaterCacheItemById } from '@helpers/cacheUpdateHelper';
import { PublishersWebsiteType, PublisherWebsiteFilterType } from '@ts/publishers.types';

import './WebsitesTable.scss';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';
import useWorkspaceMembers from '@hooks/useWorkspaceMembers';
import StubIllustration from '@uikit/old/StubIllustration/StubIllustration';

function WebsitesTable({
  isLoading,
  appliedFilters,
}: {
  isLoading: boolean;
  appliedFilters: PublisherWebsiteFilterType[];
}): JSX.Element {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const { members } = useWorkspaceMembers();
  const { page, changePage, limit, changeLimit, changeToDefault } = usePagination();
  const query = new URLSearchParams(location.search);
  const refetchParam = query.get('refetch');
  const websitesQueryKey = [
    queryCacheKeys.publisherWebsites,
    workspaceId,
    page - 1,
    limit,
    appliedFilters,
  ];

  const {
    isFetching: isWebsitesFetching,
    data: websites = [],
    error: ordersError,
    refetch: refetchWebsites,
  } = useQuery(
    websitesQueryKey,
    () => getPublisherWebsitesApi(workspaceId, page - 1, limit, appliedFilters),
    {
      enabled: !!workspaceId,
      refetchOnWindowFocus: false,
      retry: false,
      // staleTime: 5 * 1000 * 60, // 5 minutes
    }
  );

  useEffect(() => {
    if (refetchParam === 'true') {
      query.delete('refetch');
      history.replace({
        pathname: location.pathname,
        search: query.toString(),
      });
      refetchWebsites();
    }
  }, [refetchParam]);

  const mappedWebsites = useMemo(() => {
    return websites.map((website) => {
      return {
        ...website,
        createdByName:
          members.find((member) => member.userId === website.createdBy)?.fullName || '',
      };
    });
  }, [websites, members]);

  useNonInitialEffect(() => {
    if (workspaceId) {
      refetchWebsites();
      changeToDefault();
    }
  }, [workspaceId, appliedFilters]);

  useNonInitialEffect(() => {
    if (workspaceId) {
      refetchWebsites();
    }
  }, [page, limit]);

  const handleChangeStatus = (
    id: number,
    status: common_pb.PublisherWebsiteStatusMap[keyof common_pb.PublisherWebsiteStatusMap]
  ) => {
    changePublisherWebsiteStatusApi(id, workspaceId, status)
      .then(() => {
        dispatch(addNotification({ title: 'Website status updated', type: 'success' }));
        return queryClient.setQueryData(
          websitesQueryKey,
          createUpdaterCacheItemById(id, 'status', status)
        );
      })
      .catch((error) => {
        console.error(error);
        dispatch(addNotification({ title: 'Website status update failed', type: 'error' }));
      });
  };

  if (isWebsitesFetching) {
    return <Loader isLoading withTopMargin />;
  }

  // @ts-ignore
  const errorMessage = ordersError && ordersError.message;

  if (errorMessage && errorMessage.indexOf('User is not a member of workspace') !== -1) {
    return <NoAccessMessage message="Oops! You don't have access to this workspace." />;
  }

  const handleRemove = (id: number) => {
    deletePublisherWebsiteApi(workspaceId, id).then(() => {
      dispatch(addNotification({ title: 'Website deleted', type: 'success' }));
      return queryClient.setQueryData(websitesQueryKey, (oldData: PublishersWebsiteType[]) => {
        return oldData.filter((item: PublishersWebsiteType) => item.id !== id);
      });
    });
  };

  const handleEdit = (id: number) => {
    redirectUserTo(history, `${urls.WEBSITE_STEP_TWO(workspaceId, id)}`);
  };

  if (!websites || websites.length === 0) {
    const redirectToCampaignCreation = () => {
      redirectUserTo(history, urls.WEBSITE_STEP_ONE(workspaceId, ''));
    };

    return (
      <div className="orders-illustration-wrapper" data-testid="publisher-websites-empty">
        <StubIllustration
          text="Let’s add your first website :)"
          buttonText="Add a new website"
          onClickButton={redirectToCampaignCreation}
          isLoading={isWebsitesFetching}
        />
      </div>
    );
  }

  return (
    <Loader isLoading={isLoading}>
      <div className="orders-table-wrapper">
        <ConfigurableTable
          config={tableConfig}
          data={mappedWebsites}
          tableClassName="orders-table"
          rowClassName="orders-table__row"
          testId="publisher-websites-table"
          rowTestId={(row) => `publisher-websites-row-${row.domain}`}
          renderProps={{
            onUpdateStatus: handleChangeStatus,
            onRemove: handleRemove,
            onEdit: handleEdit,
          }}
          RenderRow={RenderRow}
          onRowClick={({ id }) => handleEdit(id)}
        />
      </div>
      <Pagination
        curPage={page}
        onChangePage={changePage}
        pageLimit={limit}
        onChangePageLimit={changeLimit}
        totalItems={websites[0]?.totalItems}
        testId="publisher-websites-pagination"
      />
    </Loader>
  );
}

export default WebsitesTable;
